import {
  DEFAULT_LOOT_RATE_DISPLAY_MODE,
  LOOT_RATE_DISPLAY_MODES,
} from "./loot-rate-display-preference.js";

const CONTROL_SELECTOR = "[data-loot-rate-display-mode]";

function exactMode(value) {
  if (!LOOT_RATE_DISPLAY_MODES.includes(value)) {
    throw new TypeError("Loot-rate display control mode is invalid.");
  }
  return value;
}

function positiveRevisionOrNull(value) {
  if (value === null) return null;
  if (!Number.isSafeInteger(value) || value <= 0) {
    throw new TypeError("Loot-rate display decision revision is invalid.");
  }
  return value;
}

function readDecision(repository) {
  const decision = repository.decision();
  if (
    !decision
    || typeof decision !== "object"
    || typeof decision.repositoryKind !== "string"
    || typeof decision.canChange !== "boolean"
  ) {
    throw new TypeError("Loot-rate display repository decision is invalid.");
  }
  return Object.freeze({
    repositoryKind: decision.repositoryKind,
    revision: positiveRevisionOrNull(decision.revision),
    mode: exactMode(decision.mode),
    canChange: decision.canChange,
  });
}

function controlsWithin(root) {
  const controls = root.querySelectorAll?.(CONTROL_SELECTOR);
  return controls ? Array.from(controls) : [];
}

function setBooleanAttribute(control, name, enabled) {
  if (enabled) {
    if (control.getAttribute?.(name) !== "true") {
      control.setAttribute?.(name, "true");
    }
  } else if (control.getAttribute?.(name) !== null) {
    control.removeAttribute?.(name);
  }
}

function setStringAttribute(control, name, value) {
  if (control.getAttribute?.(name) !== value) {
    control.setAttribute?.(name, value);
  }
}

function projectControlState(root, decision, commandPending) {
  const pending = commandPending || decision.repositoryKind === "pending";
  const disabled = pending || !decision.canChange;
  const conflict = decision.repositoryKind === "conflict";
  for (const control of controlsWithin(root)) {
    if (control.disabled !== disabled) {
      control.disabled = disabled;
    }
    setStringAttribute(control, "aria-disabled", disabled ? "true" : "false");
    setStringAttribute(
      control,
      "data-loot-rate-display-preference-state",
      decision.repositoryKind,
    );
    setBooleanAttribute(control, "aria-busy", pending);
    setBooleanAttribute(control, "aria-invalid", conflict);
  }
}

function invalidDecision() {
  return Object.freeze({
    repositoryKind: "invalid",
    revision: null,
    mode: DEFAULT_LOOT_RATE_DISPLAY_MODE,
    canChange: false,
  });
}

function eventControl(root, event) {
  const target = event?.target;
  const control = target?.closest?.(CONTROL_SELECTOR) ?? (
    typeof target?.getAttribute === "function"
      && target.getAttribute("data-loot-rate-display-mode") !== null
      ? target
      : null
  );
  if (!control) return null;
  if (typeof root.contains === "function" && !root.contains(control)) {
    return null;
  }
  return control;
}

export function createLootRateDisplayControlController({
  root,
  repository,
  projectMode,
  reportError = (error) => globalThis.reportError?.(error),
  createObserver = (callback) => (
    typeof globalThis.MutationObserver === "function"
      ? new globalThis.MutationObserver(callback)
      : null
  ),
} = {}) {
  if (
    !root
    || typeof root.addEventListener !== "function"
    || typeof root.removeEventListener !== "function"
    || typeof root.querySelectorAll !== "function"
  ) {
    throw new TypeError("Loot-rate display controls require a DOM root.");
  }
  if (
    !repository
    || typeof repository.decision !== "function"
    || typeof repository.setMode !== "function"
    || typeof repository.subscribe !== "function"
  ) {
    throw new TypeError(
      "Loot-rate display controls require the typed preference repository.",
    );
  }
  if (
    typeof projectMode !== "function"
    || typeof reportError !== "function"
    || typeof createObserver !== "function"
  ) {
    throw new TypeError(
      "Loot-rate display controls require projection and error callbacks.",
    );
  }

  let started = false;
  let generation = 0;
  let commandPending = false;
  let unsubscribe = null;
  let observer = null;
  let projectedMode = null;

  function renderDecision() {
    if (!started) return null;
    let decision;
    try {
      decision = readDecision(repository);
    } catch (error) {
      reportError(error);
      decision = invalidDecision();
    }
    projectControlState(root, decision, commandPending);
    if (decision.mode !== projectedMode) {
      try {
        projectMode(decision.mode);
        projectedMode = decision.mode;
      } catch (error) {
        reportError(error);
        const failed = invalidDecision();
        projectControlState(root, failed, commandPending);
        return failed;
      }
    }
    return decision;
  }

  function handleRepositoryChange() {
    try {
      const decision = readDecision(repository);
      if (decision.repositoryKind !== "pending") commandPending = false;
    } catch (_error) {
      commandPending = false;
    }
    renderDecision();
  }

  function refreshControls() {
    if (!started) return;
    try {
      projectControlState(root, readDecision(repository), commandPending);
    } catch (error) {
      reportError(error);
      projectControlState(root, invalidDecision(), commandPending);
    }
  }

  function finishCommand(startGeneration) {
    if (!started || generation !== startGeneration) return;
    commandPending = false;
    renderDecision();
  }

  function handleClick(event) {
    const control = eventControl(root, event);
    if (!control) return;
    event.preventDefault?.();
    if (commandPending) return;

    let decision;
    let mode;
    try {
      decision = readDecision(repository);
      mode = exactMode(control.getAttribute("data-loot-rate-display-mode"));
    } catch (error) {
      reportError(error);
      return;
    }
    if (!decision.canChange || decision.repositoryKind === "pending") {
      renderDecision();
      return;
    }
    if (mode === decision.mode) return;

    commandPending = true;
    projectControlState(root, decision, true);
    const startGeneration = generation;
    let command;
    try {
      command = repository.setMode(mode, {
        expectedRevision: decision.revision,
      });
    } catch (error) {
      reportError(error);
      finishCommand(startGeneration);
      return;
    }
    Promise.resolve(command)
      .catch((error) => reportError(error))
      .finally(() => finishCommand(startGeneration));
  }

  function start() {
    if (started) return readDecision(repository);
    started = true;
    generation += 1;
    root.addEventListener("click", handleClick);
    try {
      unsubscribe = repository.subscribe(handleRepositoryChange);
      if (typeof unsubscribe !== "function") {
        throw new TypeError(
          "Loot-rate display repository subscription is not cancellable.",
        );
      }
      observer = createObserver(refreshControls);
      if (observer !== null) {
        if (
          typeof observer.observe !== "function"
          || typeof observer.disconnect !== "function"
        ) {
          throw new TypeError(
            "Loot-rate display control observer is not cancellable.",
          );
        }
        observer.observe(root, { childList: true, subtree: true });
      }
      return renderDecision();
    } catch (error) {
      root.removeEventListener("click", handleClick);
      unsubscribe?.();
      unsubscribe = null;
      observer?.disconnect?.();
      observer = null;
      started = false;
      generation += 1;
      throw error;
    }
  }

  function stop() {
    if (!started) return;
    started = false;
    generation += 1;
    commandPending = false;
    root.removeEventListener("click", handleClick);
    unsubscribe?.();
    unsubscribe = null;
    observer?.disconnect?.();
    observer = null;
  }

  return Object.freeze({ start, stop });
}
